import React, { useState, useEffect } from 'react';
import { Building, Star, Check, Users, Calendar, MapPin, ArrowRight, X, ExternalLink, ChevronLeft, ChevronRight, Image } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PackagesDisplay = ({ onEnquire }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGallery, setShowGallery] = useState(null); // {type: 'makkah'|'madinah', pkg: package}
  const [galleryIndex, setGalleryIndex] = useState(0);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await fetch(`${API_URL}/api/packages/`);
        const data = await response.json();
        setPackages(data.packages || []);
      } catch (error) {
        console.error('Error fetching packages:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, []);

  // Render stars
  const renderStars = (count) => {
    return Array(count).fill(0).map((_, i) => (
      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
    ));
  };

  // Get hotel images
  const getHotelImages = () => {
    if (!showGallery) return [];
    const hotel = showGallery.type === 'makkah' ? showGallery.pkg.makkah_hotel : showGallery.pkg.madinah_hotel;
    return hotel?.images || [];
  };

  // Open gallery
  const openGallery = (pkg, type) => {
    const hotel = type === 'makkah' ? pkg.makkah_hotel : pkg.madinah_hotel;
    if (hotel?.images && hotel.images.length > 0) {
      setShowGallery({ type, pkg });
      setGalleryIndex(0);
    }
  };

  if (loading) {
    return (
      <section id="packages" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-64 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-96 mx-auto"></div>
          </div>
        </div>
      </section>
    );
  }

  if (packages.length === 0) {
    return null;
  }

  return (
    <section id="packages" className="py-16 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1 bg-[#d4af37]/20 text-[#d4af37] rounded-full text-sm font-semibold mb-4">
            Our Packages
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-[#0a1628] mb-4">
            Umrah Packages
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Choose from our carefully curated Umrah packages designed to make your spiritual journey comfortable and memorable
          </p>
        </div>

        {/* Packages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {packages.map((pkg) => (
            <div 
              key={pkg.package_id}
              className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300 border border-gray-100"
            >
              {/* Image */}
              <div className="relative h-52 bg-gray-200">
                {pkg.image_url ? (
                  <img 
                    src={pkg.image_url} 
                    alt={pkg.name} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#0a1628] to-[#1a365d] flex items-center justify-center">
                    <span className="text-white text-xl font-bold">{pkg.name}</span>
                  </div>
                )}
                <div className="absolute top-4 left-4 bg-[#d4af37] text-[#0a1628] px-4 py-1.5 rounded-full text-sm font-bold shadow-lg">
                  {pkg.duration_days} Days / {pkg.duration_nights} Nights
                </div>
                {pkg.spots_booked >= pkg.spots_total * 0.8 && (
                  <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse">
                    Almost Full!
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-2xl font-bold text-[#0a1628] mb-4">{pkg.name}</h3>

                {/* Hotels */}
                <div className="space-y-3 mb-5">
                  {/* Makkah Hotel */}
                  <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl">
                    <div className="w-10 h-10 bg-[#d4af37] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Building className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-[#0a1628] text-sm">MAKKAH</span>
                        <span className="text-xs bg-[#d4af37] text-white px-2 py-0.5 rounded-full font-semibold">
                          {pkg.makkah_hotel?.nights || 5} Nights
                        </span>
                      </div>
                      <div className="text-sm text-gray-700 font-medium truncate">
                        {pkg.makkah_hotel?.name || 'Premium Hotel'}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex">
                          {renderStars(pkg.makkah_hotel?.stars || 4)}
                        </div>
                        <span className="text-xs text-gray-500">
                          ({pkg.makkah_hotel?.sharing_type || 'QUAD SHARING'})
                        </span>
                      </div>
                      {/* Hotel Actions */}
                      <div className="flex items-center gap-2 mt-2">
                        {pkg.makkah_hotel?.images?.length > 0 && (
                          <button
                            onClick={() => openGallery(pkg, 'makkah')}
                            className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full hover:bg-amber-200 flex items-center gap-1"
                          >
                            <Image className="w-3 h-3" />
                            View Photos ({pkg.makkah_hotel.images.length})
                          </button>
                        )}
                        {pkg.makkah_hotel?.website_url && (
                          <a
                            href={pkg.makkah_hotel.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full hover:bg-blue-200 flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Website
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Madinah Hotel */}
                  <div className="flex items-start gap-3 p-3 bg-green-50 rounded-xl">
                    <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Building className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-[#0a1628] text-sm">MADINAH</span>
                        <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full font-semibold">
                          {pkg.madinah_hotel?.nights || 4} Nights
                        </span>
                      </div>
                      <div className="text-sm text-gray-700 font-medium truncate">
                        {pkg.madinah_hotel?.name || 'Premium Hotel'}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex">
                          {renderStars(pkg.madinah_hotel?.stars || 4)}
                        </div>
                        <span className="text-xs text-gray-500">
                          ({pkg.madinah_hotel?.sharing_type || 'QUAD SHARING'})
                        </span>
                      </div>
                      {/* Hotel Actions */}
                      <div className="flex items-center gap-2 mt-2">
                        {pkg.madinah_hotel?.images?.length > 0 && (
                          <button
                            onClick={() => openGallery(pkg, 'madinah')}
                            className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full hover:bg-green-200 flex items-center gap-1"
                          >
                            <Image className="w-3 h-3" />
                            View Photos ({pkg.madinah_hotel.images.length})
                          </button>
                        )}
                        {pkg.madinah_hotel?.website_url && (
                          <a
                            href={pkg.madinah_hotel.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full hover:bg-blue-200 flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Website
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Inclusions */}
                <div className="flex flex-wrap gap-2 mb-5">
                  {(pkg.inclusions || []).slice(0, 4).map((inc, i) => (
                    <span 
                      key={i} 
                      className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2.5 py-1 rounded-full"
                    >
                      <Check className="w-3 h-3 text-green-500" />
                      {inc}
                    </span>
                  ))}
                  {(pkg.inclusions || []).length > 4 && (
                    <span className="text-xs text-gray-500 px-2 py-1">
                      +{pkg.inclusions.length - 4} more
                    </span>
                  )}
                </div>

                {/* Price & Spots */}
                <div className="flex items-end justify-between mb-5 pb-5 border-b border-gray-100">
                  <div>
                    {pkg.original_price > pkg.discounted_price && (
                      <span className="text-gray-400 line-through text-lg">
                        £{pkg.original_price?.toLocaleString()}
                      </span>
                    )}
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-[#d4af37]">
                        £{pkg.discounted_price?.toLocaleString()}
                      </span>
                      <span className="text-gray-500 text-sm">/person</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500">
                      {pkg.spots_booked}/{pkg.spots_total} booked
                    </div>
                    <div className="w-24 h-2 bg-gray-200 rounded-full mt-1">
                      <div 
                        className="h-2 bg-[#d4af37] rounded-full transition-all duration-500"
                        style={{ width: `${(pkg.spots_booked / pkg.spots_total) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* CTA Button */}
                <button
                  onClick={() => onEnquire && onEnquire(pkg)}
                  className="w-full py-3.5 bg-[#0a1628] text-white rounded-xl font-semibold hover:bg-[#1a365d] transition-colors flex items-center justify-center gap-2 group"
                >
                  Book Now
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">
            Can't find what you're looking for? Let us create a custom package for you!
          </p>
          <a 
            href="#enquiry-form"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#d4af37] text-[#0a1628] rounded-xl font-semibold hover:bg-[#c9a432] transition-colors"
          >
            Request Custom Package
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Hotel Images Gallery Modal */}
      {showGallery && getHotelImages().length > 0 && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setShowGallery(null)}>
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            {/* Close button */}
            <button
              onClick={() => setShowGallery(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 p-2"
            >
              <X className="w-8 h-8" />
            </button>

            {/* Hotel name */}
            <div className="text-center mb-4">
              <h3 className="text-white text-xl font-bold">
                {showGallery.type === 'makkah' 
                  ? showGallery.pkg.makkah_hotel?.name 
                  : showGallery.pkg.madinah_hotel?.name
                }
              </h3>
              <p className="text-gray-400 text-sm">
                {showGallery.type === 'makkah' ? 'Makkah Hotel' : 'Madinah Hotel'} - Photo {galleryIndex + 1} of {getHotelImages().length}
              </p>
            </div>

            {/* Main Image */}
            <div className="relative bg-black rounded-lg overflow-hidden">
              <img
                src={getHotelImages()[galleryIndex]}
                alt={`Hotel photo ${galleryIndex + 1}`}
                className="w-full h-[60vh] object-contain"
              />

              {/* Navigation arrows */}
              {getHotelImages().length > 1 && (
                <>
                  <button
                    onClick={() => setGalleryIndex((prev) => (prev === 0 ? getHotelImages().length - 1 : prev - 1))}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white p-3 rounded-full transition-colors"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={() => setGalleryIndex((prev) => (prev === getHotelImages().length - 1 ? 0 : prev + 1))}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white p-3 rounded-full transition-colors"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}
            </div>

            {/* Thumbnails */}
            {getHotelImages().length > 1 && (
              <div className="flex justify-center gap-2 mt-4 overflow-x-auto py-2">
                {getHotelImages().map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setGalleryIndex(idx)}
                    className={`flex-shrink-0 w-16 h-12 rounded overflow-hidden border-2 transition-all ${
                      idx === galleryIndex ? 'border-[#d4af37] scale-110' : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default PackagesDisplay;
